import { ShieldCheck } from "lucide-react";
import { Navigate } from "react-router";

import { PageHeader } from "@/components/page-header";
import { useMe } from "@/lib/api";

/**
 * Portal entry point. Sends a customer to the first section their capabilities
 * unlock (Permissions, then Approvals). A customer with neither capability sees
 * a friendly "nothing to manage" landing rather than an empty shell.
 */
export function PortalHome() {
  const { data: me } = useMe();

  // The route guard guarantees a signed-in customer before this renders; the
  // null check only satisfies the type while `me` settles.
  if (!me) {
    return null;
  }

  if (me.capabilities?.manage_account) {
    return <Navigate to="/portal/permissions" replace />;
  }
  if (me.capabilities?.approve_orders) {
    return <Navigate to="/portal/approvals" replace />;
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
      <PageHeader
        eyebrow={me.account_name ?? "Customer portal"}
        title={`Welcome, ${me.first_name?.trim() || me.email}`}
        description="You're signed in to your organization's collaboration portal."
      />
      <div className="bg-card border-border flex items-start gap-3 rounded-lg border p-5 shadow-sm">
        <span className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-md">
          <ShieldCheck className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="text-foreground text-sm font-medium">Nothing to manage right now</p>
          <p className="text-muted mt-1 text-sm leading-relaxed">
            Your J&amp;J contact hasn&apos;t granted you self-service permissions yet. Reach out to
            them if you expect access to manage colleagues or approve orders.
          </p>
        </div>
      </div>
    </div>
  );
}
